import React, { useState, useRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { CrmAutocomplete } from '../crm';
import { useCrmTenant } from '../crm/useCrmTenant';

const StyledWrapper = styled.div`
  width: 100%;
  margin: 8px 0;

  .crm-autocomplete-error-text {
    color: #d32f2f;
    font-size: 0.75rem;
    margin-top: 3px;
  }
`;

interface CrmAutocompleteData {
  field_name?: string;
  required?: boolean;
  value?: string;
  placeholder?: string;
  /** Which CRM list to search: 'segment' | 'channel' | 'lead'. */
  crmSource?: string;
  /** Source-specific filters, e.g. { type: 'SMTP' } for channels. */
  crmFilters?: Record<string, string>;
}

interface CrmAutocompleteInputProps {
  data: CrmAutocompleteData;
  onChange?: (value: Record<string, string>) => void;
  read_only?: boolean;
  /** false on the builder canvas, true in the rendered form. */
  mutable?: boolean;
}

/**
 * Form field that searches the tenant's CRM (segments, channels, ...). The
 * submitted value is the selected item's id. What it searches and how it is
 * filtered is configured per field in the builder's edit panel.
 */
const CrmAutocompleteInput = React.forwardRef<
  HTMLInputElement,
  CrmAutocompleteInputProps
>((props, ref) => {
  const { field_name, required, value: initialValue, placeholder, crmSource, crmFilters } =
    props.data || {};
  const tenantId = useCrmTenant();

  const [value, setValue] = useState<string>(initialValue || '');
  const [touched, setTouched] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

  // The builder canvas only previews the control; don't hit the CRM there.
  const isLive = props.mutable !== false;
  const showError = isLive && touched && required && !value;
  const tenantMissing = isLive && !tenantId;

  const emitChange = (next: string) => {
    setValue(next);
    setTouched(true);
    if (props.onChange && field_name) {
      props.onChange({ [field_name]: next });
    }
  };

  return (
    <StyledWrapper>
      <CrmAutocomplete
        tenantId={tenantId}
        source={crmSource}
        filters={crmFilters}
        value={value || null}
        onChange={(id) => emitChange(id || '')}
        placeholder={placeholder}
        disabled={props.read_only || !isLive || tenantMissing}
        enabled={isLive}
        error={Boolean(showError)}
      />
      {tenantMissing && (
        <div className="crm-autocomplete-error-text">
          The tenant for this field is not configured.
        </div>
      )}
      {showError && (
        <div className="crm-autocomplete-error-text">This field is required.</div>
      )}
      <input ref={hiddenInputRef} type="hidden" name={field_name} value={value} />
    </StyledWrapper>
  );
});

CrmAutocompleteInput.displayName = 'CrmAutocompleteInput';
export default CrmAutocompleteInput;
